import React from "react";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { RootStackParamList } from "../../types/navigationParams";
import DynamicForm from "../../components/DynamicForm";
import { DrawerScreenProps } from "@react-navigation/drawer";
import { updateTimestampAction } from "../../redux/slices/authSlice";
import { IconButton, useTheme } from "react-native-paper";
import { addDocument, update } from "../../services/firestoreServices";
import { getStatusAt, validateFirestoreData } from "../../utils/utils";
import { StyleSheet, View } from "react-native";
import { serverTimestamp, Timestamp } from "../../firebase";
import { useScreenSize } from "../../hooks/useScreenSize";
import ModalScreenWrapper from "../../components/ModalScreenWrapper";

import { Collection } from "../../config/companies/config";

type Props = DrawerScreenProps<RootStackParamList, "FormScreen">;

const convertTimestamps = (obj: any) => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (value instanceof Timestamp) {
        return [key, value.toDate()];
      }
      return [key, value];
    }),
  );
};

export default function FormScreen({ route, navigation }: Props) {
  const {
    item,
    collection: collectionName,
    onNewDocumentCallback,
  } = route.params;
  const user = useAppSelector((state) => state.auth.user);
  const config = useAppSelector((state) => state.auth.config);
  const dispatch = useAppDispatch();
  const collections = config?.database as Collection[];
  const selectedCollection = collections.find(
    (c) => c.name === collectionName,
  ) as Collection;
  const fields = selectedCollection.fields;
  const theme = useTheme();
  const { LARGE_SCREEN } = useScreenSize();
  const preparedItem = item ? convertTimestamps(item) : undefined;

  const collection = config.database.find(
    (item) => item.name === collectionName,
  );

  if (!collection) {
    throw new Error(`Collection ${collectionName} not found in config`);
  }

  // Check if user has permission to delete based on collection configuration
  const canDelete = collection.deletableBy
    ? collection.deletableBy.includes(user.role)
    : true;

  const deleteItem = () => {
    if (item) {
      navigation.navigate("DeleteConfirmationScreen", {
        item: item,
        collection: collectionName,
      });
    }
  };

  const handleSubmit = async (data: any) => {
    console.log("handle submit", data);
    const collectionPicker = fields.find(
      (item) => item.type === "collectionPicker",
    );

    if (collectionPicker) {
      const fieldName = collectionPicker["name"];
      if (collectionPicker?.onlyId) {
        if (collectionPicker.multiselect) {
          data[fieldName + "Id"] =
            data[fieldName].length &&
            data[fieldName].map((item: { id: string }) => item.id);
        } else {
          data[fieldName + "Id"] = data[fieldName].id;
        }
      }
    }

    if (!validateFirestoreData(data)) {
      console.error("has undefined");
      return;
    }

    try {
      if (item) {
        // Проверяем изменение статуса
        const oldStatus = item.status;
        const newStatus = data.status;
        if (
          typeof oldStatus !== "undefined" &&
          typeof newStatus !== "undefined" &&
          oldStatus !== newStatus
        ) {
          data.statusChangedAt = serverTimestamp();
          data[getStatusAt(newStatus)] = serverTimestamp();
        }
        await update(collection.name, item.id, data);
        dispatch(updateTimestampAction());
      } else {
        const createdAt = serverTimestamp();
        const index = Date.now();
        const documentData = {
          ...data,
          createdById: user.uid,
          createdBy: user,
          company: data.companyId ? data.companyId : user.company,
          companyId: data.companyId ? data.companyId : user.company?.id,
          createdAt,
          index,
          deleted: false,
        };

        // Handle files collection - extract file URL and set dateAdded
        if (collectionName === "files") {
          // Extract file URL from uploaded files array
          if (
            data.fileUrl &&
            Array.isArray(data.fileUrl) &&
            data.fileUrl.length > 0
          ) {
            const firstFile = data.fileUrl[0];
            documentData.fileUrl =
              typeof firstFile === "string"
                ? firstFile
                : firstFile.uri || firstFile;
          }
          // Set dateAdded to current timestamp
          documentData.dateAdded = serverTimestamp();
        }

        const newDoc = await addDocument(collection, documentData);
        dispatch(updateTimestampAction());

        if (onNewDocumentCallback) {
          onNewDocumentCallback({
            ...documentData,
            createdAt: new Date(),
            id: newDoc.id,
          });
          return;
        }
      }
      navigation.goBack();
    } catch (error) {
      return error;
    }
  };

  React.useEffect(() => {
    if (!LARGE_SCREEN && item && canDelete) {
      navigation.setOptions({
        headerRight: () => (
          <IconButton
            icon="delete"
            onPress={deleteItem}
            iconColor={theme.colors.error}
            accessibilityLabel="Delete"
          />
        ),
      });
    }
  }, [LARGE_SCREEN, item, canDelete]);

  const renderActionButtons = () => {
    return (
      <View style={styles.headerButtons}>
        <IconButton icon="arrow-left" onPress={() => navigation.goBack()} />
        {item && canDelete && (
          <IconButton
            icon="delete"
            onPress={deleteItem}
            iconColor={theme.colors.error}
          />
        )}
      </View>
    );
  };

  return (
    <ModalScreenWrapper
      onClose={() => navigation.goBack()}
      renderActionButtons={renderActionButtons}
    >
      <View
        style={{
          backgroundColor: theme.colors.background,
        }}
      >
        <DynamicForm
          fields={fields}
          handleSubmit={handleSubmit}
          existingData={preparedItem}
          collectionName={collectionName}
          collection={collection}
        />
      </View>
    </ModalScreenWrapper>
  );
}

const styles = StyleSheet.create({
  headerButtons: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  mobileDeleteButton: {
    alignItems: "flex-end",
  },
});
